import { ReactNode, useEffect } from 'react';
import { fallbackFlags } from 'config/launch-darkly-flags';
import { camelCaseKeys } from 'launchdarkly-react-client-sdk';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector, shallowEqual } from 'react-redux';

import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  getAdobeTargetOfferThunk,
  TExperimentFlag
} from 'state/analytics/analytics-thunks';
import { selectAdobeTargetExperiments } from 'state/analytics/analytics-slice';
import { AdobeTargetContext } from 'components/app/AppContext';
import PrivacyService from 'services/PrivacyService';

// Get all experiment flag names
const getExperimentFlags = (): [string, unknown][] => {
  return Object.entries(fallbackFlags).filter(
    // naming convention: only 'experiment-' flags are fetched from adobe target
    ([key]) => key.startsWith('experiment-') || key === 'release-abandoned-cart' // temporary naming convention exception for release-abandon-cart
  );
};

/**
 * Provider that fetches Adobe Target experiments in the background after app renders
 * Components using useExperiments() will automatically rerender when experiments load
 * Uses Redux thunks for fetching to maintain compatibility with tests and debugging
 */
export const AdobeTargetProvider = ({ children }: { children: ReactNode }) => {
  const adobeExperiments = useSelector(
    selectAdobeTargetExperiments,
    shallowEqual
  );
  const dispatch = useAppDispatch();
  const ldFlags = useFlags();

  // Fetch Adobe Target experiments in the background after mount
  const privacyChangeHandler = () => {
    const experimentFlags = getExperimentFlags();

    // Dispatch all thunks in parallel (non-blocking)
    Promise.allSettled(
      experimentFlags.map(([experimentName]) =>
        dispatch(getAdobeTargetOfferThunk(experimentName as TExperimentFlag))
      )
    );
  };

  useEffect(() => {
    PrivacyService.registerCallback(privacyChangeHandler);
    privacyChangeHandler();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Merge flags: Adobe Target experiments override LaunchDarkly flags
  const camelCasedExperiments = camelCaseKeys(adobeExperiments);
  const mergedFlags = Object.entries({ ...camelCaseKeys(ldFlags) }).reduce(
    (acc, ldFlag) => {
      const [ldFlagName, ldFlagValue] = ldFlag;
      const atExperimentValue = camelCasedExperiments[ldFlagName]?.value;

      return {
        ...acc,
        [ldFlagName]:
          atExperimentValue !== undefined ? atExperimentValue : ldFlagValue
      };
    },
    {}
  );

  return (
    <AdobeTargetContext.Provider value={mergedFlags}>
      {children}
    </AdobeTargetContext.Provider>
  );
};

AdobeTargetProvider.displayName = 'AdobeTargetProvider';
